import React, { useState, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';

const LetterCountPage = () => {
  const [text, setText] = useState('');
  const [stats, setStats] = useState({
    letters: 0,
    lettersNoSpaces: 0,
    words: 0,
    sentences: 0,
    paragraphs: 0,
    lines: 0
  });
  
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    
    // Calculate statistics
    const letters = newText.length;
    const lettersNoSpaces = newText.replace(/\s/g, '').length;
    const words = newText.trim() ? newText.trim().split(/\s+/).length : 0;
    const sentences = newText.split(/[.!?]+/).filter(Boolean).length;
    const paragraphs = newText.split(/\n+/).filter(Boolean).length;
    const lines = newText.split('\n').length;
    
    setStats({
      letters,
      lettersNoSpaces,
      words,
      sentences,
      paragraphs,
      lines
    });
  };

  const handleScroll = () => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const clearText = () => {
    setText('');
    setStats({
      letters: 0,
      lettersNoSpaces: 0,
      words: 0,
      sentences: 0,
      paragraphs: 0,
      lines: 0
    });
  };

  return (
    <div className="container" style={{ width: '90%', maxWidth: '1400px' }}>
      <SEO
        title="Letter Count Tool | YourJSONTools"
        description="Count letters, words, sentences, paragraphs, and lines in your text. Free online text analyzer."
        keywords="letter count, character count, word count, text analyzer, sentence counter, paragraph counter"
      />

      <h1>Letter Count Tool</h1>
      <p className="tool-description">
        Analyze your text to count letters, words, sentences, paragraphs, and lines. A simple but powerful text statistics tool.
      </p>

      <div className="letter-count-container">
        <div className="tool-section">
          <div className="editor-header">
            <h3>Enter Text</h3>
            <div className="toolbar">
              <button onClick={clearText} className="toolbar-button clear-button">Clear</button>
            </div>
          </div>
          
          <div className="editor-wrapper">
            <TextareaWithLines
              value={text}
              onChange={handleTextChange}
              onScroll={handleScroll}
              textareaRef={textareaRef}
              lineNumbersRef={lineNumbersRef}
              placeholder="Type or paste your text here..."
            />
          </div>
        </div>

        <div className="stats-section">
          <h3>Text Statistics</h3>
          
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{stats.letters}</div>
              <div className="stat-label">Letters (with spaces)</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-value">{stats.lettersNoSpaces}</div>
              <div className="stat-label">Letters (no spaces)</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-value">{stats.words}</div>
              <div className="stat-label">Words</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-value">{stats.sentences}</div>
              <div className="stat-label">Sentences</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-value">{stats.paragraphs}</div>
              <div className="stat-label">Paragraphs</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-value">{stats.lines}</div>
              <div className="stat-label">Lines</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .letter-count-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }
        
        @media (min-width: 1024px) {
          .letter-count-container {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        .tool-section {
          display: flex;
          flex-direction: column;
          background: #f5f5f5;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: #eaeaea;
          border-bottom: 1px solid #ddd;
        }
        
        .editor-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #333;
        }
        
        .toolbar {
          display: flex;
          gap: 8px;
        }
        
        .toolbar-button {
          padding: 0.4rem 0.8rem;
          font-size: 0.875rem;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .toolbar-button:hover {
          background: #f0f0f0;
        }
        
        .copy-button {
          background: #4caf50;
          color: white;
          border-color: #4caf50;
        }
        
        .copy-button:hover {
          background: #388e3c;
        }
        
        .clear-button {
          background: #607d8b;
          color: white;
          border-color: #607d8b;
        }
        
        .clear-button:hover {
          background: #455a64;
        }
        
        .editor-wrapper {
          flex: 1;
          min-height: 400px;
          position: relative;
        }
        
        .stats-section {
          background: #f5f5f5;
          border-radius: 6px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
        }
        
        .stats-section h3 {
          margin-top: 0;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
          font-weight: 600;
          color: #333;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
          gap: 1.5rem;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 1rem;
          background: white;
          border-radius: 6px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
        }
        
        .stat-value {
          font-size: 2rem;
          font-weight: bold;
          color: #2196f3;
          margin-bottom: 0.5rem;
        }
        
        .stat-label {
          font-size: 0.875rem;
          color: #666;
        }
      `}</style>

      <AdUnit />
    </div>
  );
};

export default LetterCountPage; 